import * as React from 'react'
import GoogleMap from 'google-map-react'
import Loader from 'react-loader-spinner'

import config from '@app/config'

import MarkerChantier from '@components/marker/business'
import MarkerOrphan from '@components/marker/orphan'
import BusinessCluster from '@components/cluster/cluster'
import BusinessLegend from '@components/legend/BusinessLegend'

import useReducer from '@store/useReducer'
import * as LocalisationStore from '@store/localisation/localisation'

import styled from '@theme/styled-components'
import Theme from '@theme/Theme'

interface Props {
  navigation: Navigation
  orphansVisible: boolean
  clusters: Cluster[]
  isolatedBusinesses: BusinessLocation[]
  orphans: OrphanDeclarationResume[]
  screenStatus: ScreenStatus
}

const BusinessesMap = ({ navigation, orphansVisible, orphans, screenStatus, clusters, isolatedBusinesses }: Props) => {
  const mapRef = React.useRef()

  const mapZoom = useReducer(LocalisationStore.store, (s) => s.mapZoom)
  const center = useReducer(LocalisationStore.store, (s) => s.center)

  // Legende de la carte
  const [legendOpened, setLegendOpened] = React.useState<boolean>(false)

  //TODO: ajouter système faisant qu'on fait un appel serveur à chaque changement de coordonées
  const mapClusters: MapPoint[] = React.useMemo(
    () =>
      clusters.map((cluster) => ({
        type: 'Feature',
        properties: {
          cluster: true,
          value: cluster.value,
        },
        geometry: {
          type: 'Point',
          coordinates: [cluster.longitude, cluster.latitude],
        },
      })),
    [clusters]
  )
  const mapIsolatedBusinesses: MapPoint[] = React.useMemo(
    () =>
      isolatedBusinesses.map((business) => ({
        type: 'Feature',
        properties: {
          cluster: false,
          id: business.id,
          state: business.state,
          source: business.source,
        },
        geometry: {
          type: 'Point',
          coordinates: [business.longitude, business.latitude],
        },
      })),
    [isolatedBusinesses]
  )

  const mapBusinesses: MapPoint[] = React.useMemo(
    () => [...mapClusters, ...mapIsolatedBusinesses],
    [mapClusters, mapIsolatedBusinesses]
  )

  const orphansPoints: MapPoint[] = React.useMemo(
    () =>
      orphans.map((orphan) => ({
        type: 'Feature',
        properties: {
          cluster: false,
          id: orphan.id,
          state: 'orphan',
          title: undefined,
          photoCount: orphan.pictureNumber,
        },
        geometry: {
          type: 'Point',
          coordinates: [orphan.longitude, orphan.latitude],
        },
      })),
    [orphans]
  )

  const mapPoints: MapPoint[] = React.useMemo(
    () => (orphansVisible ? [...mapBusinesses, ...orphansPoints] : mapBusinesses),
    [orphansVisible, orphansPoints, mapBusinesses]
  )

  React.useEffect(() => {
    updateBounds()
  }, [mapRef.current])

  const updateBounds = () => {
    const map: any = mapRef.current
    if (map) {
      const mapBounds = map.getBounds()
      if (mapBounds) {
        LocalisationStore.actions.setSouthWestBound({
          lat: mapBounds.getSouthWest().lat(),
          lng: mapBounds.getSouthWest().lng(),
        })
        LocalisationStore.actions.setNorthEastBound({
          lat: mapBounds.getNorthEast().lat(),
          lng: mapBounds.getNorthEast().lng(),
        })
      }
    }
  }

  const renderMarker = (mapPoint: MapPoint, index: number) => {
    const [longitude, latitude] = mapPoint.geometry.coordinates
    const { cluster: isCluster } = mapPoint.properties
    if (isCluster) {
      return (
        <BusinessCluster
          key={`${mapPoint.properties.value} - ${index}`}
          lat={latitude}
          lng={longitude}
          size={40}
          count={mapPoint.properties.value}
          onClick={() => {
            if (mapZoom) {
              const expansionZoom = mapZoom + 1
              const map: any = mapRef.current
              map && map.setZoom(expansionZoom)
              map && map.panTo({ lat: latitude, lng: longitude })
            }
          }}
        />
      )
    }
    if (mapPoint.properties.state === 'orphan') {
      return (
        <MarkerOrphan
          key={`${mapPoint.properties.id} - ${index}`}
          size={60}
          lat={latitude}
          lng={longitude}
          focused={false}
          onClick={() => navigation.push(`/focus/orphan/${mapPoint.properties.id}`)}
          photoCount={mapPoint.properties.photoCount}
        />
      )
    }
    return (
      <MarkerChantier
        size={40}
        key={`${mapPoint.properties.id} - ${index}`}
        lat={latitude}
        lng={longitude}
        type={mapPoint.properties.state}
        source={mapPoint.properties.source}
        onClick={() => navigation.push(`/focus/business/${mapPoint.properties.id}`)}
        //TODO: Voir si titre ici important et demander à le récupérer en back si oui
        title={mapPoint.properties.title || 'TITRE TEMPORAIRE'}
        focused={false}
        hideTitle={true}
      />
    )
  }

  return (
    <Container id="map-div">
      <GoogleMap
        bootstrapURLKeys={{ key: config.GMAP_APIKEY }}
        defaultCenter={center}
        defaultZoom={mapZoom}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={({ map }) => {
          mapRef.current = map
        }}
        onDragEnd={updateBounds}
        onZoomAnimationEnd={updateBounds}
        onChange={({ zoom, center }) => {
          LocalisationStore.actions.setMapZoom(zoom)
          LocalisationStore.actions.setCenter(center)
        }}
        options={{ fullscreenControl: false }}>
        {screenStatus === 'loading' ? (
          <StatusContainer>
            <Loader type="ThreeDots" color={Theme.colors.mapButtonLoader} height={30} />
          </StatusContainer>
        ) : (
          mapPoints.map(renderMarker)
        )}
      </GoogleMap>
      <BusinessLegend isOpened={legendOpened} setIsOpened={setLegendOpened} source="home" />
    </Container>
  )
}

export default BusinessesMap

const Container = styled('div')`
  display: flex;
  flex: 1;
`

const StatusContainer = styled('div')`
  position: absolute;
`
